import habService from '@/services/habService';
import { User, UserApp } from '@/types/user';
import { defineStore } from 'pinia';
import userService from '@/services/userService';
import { UserInDb } from '@/types/UserInDb';
import lienUtilService from '@/services/lienUtilService';
import type {
  CreateUserDTO,
  UpdateUserDTO,
  PreferencesDto,
  LienResponse,
} from '@/types/api';

// Create a new store instance
export const UseStoreUser = defineStore('user', {
  state: () => ({
    user: {} as User,
    userRoles: {} as Record<string, { roles?: string[] }>,
    /** User apps from API: label, pictoName, link (pictos from DB via hub-api) */
    userApps: [] as UserApp[],
    userInDb: {} as UserInDb,
    userLinks: [] as LienResponse[],
  }),
  getters: {
    /** True if token resource_access has at least one role for hub-back (e.g. hub-user, hub-admin). Case-insensitive client id. */
    hasAnyHubRole(): boolean {
      const key = Object.keys(this.userRoles).find(
        k => k.toLowerCase() === 'hub-back'
      );
      const roles = key ? this.userRoles[key]?.roles : undefined;
      return Array.isArray(roles) && roles.length > 0;
    },
  },
  actions: {
    loadUser(user: User) {
      this.user = user;
    },
    setUserRoles(userRoles: Record<string, { roles?: string[] }>) {
      this.userRoles = userRoles;
    },
    /** Load user apps (products use fixed display values, no translation). */
    async getUserApps() {
      await habService.loadUserApps(
        this.user.id,
        (apps: UserApp[]) => {
          this.userApps = apps;
        },
        () => {
          this.userApps = [];
        }
      );
    },
    loadUserFavorite(userInDb: UserInDb) {
      this.userInDb = userInDb;
    },
    async fetchUserData() {
      await userService.loadUserData();
      if (!this.userInDb.id && this.user.id) {
        await this.createUser({
          id: this.user.id,
          username: this.user.username,
        });
      }
    },
    async createUser(userData: CreateUserDTO) {
      await userService.createUser(
        userData,
        (response: UserInDb) => {
          this.userInDb = response;
          console.log('User created successfully');
        },
        () => {
          console.error('Failed to create user');
        }
      );
    },
    async updateFavorite(userData: { id: string } & UpdateUserDTO) {
      await userService.updateFavorite(
        userData,
        () => {
          console.log('Favorite Updated  successfully');
        },
        () => {
          console.error('Failed to Update Favorite ');
        }
      );
    },
    async updatePreferences(userId: string, preferences: PreferencesDto) {
      await userService.updatePreferences(
        userId,
        preferences,
        result => {
          this.userInDb.preferences = result;
          console.log('Preferences updated in store');
        },
        () => {
          console.error('Failed to update preferences');
        }
      );
    },
    loadUserLinks(links: LienResponse[]) {
      this.userLinks = links;
    },

    async fetchUserLinks() {
      const userId = this.userInDb.id;
      if (!userId) {
        console.warn('User ID is missing');
        return;
      }

      await lienUtilService.loadUserLinks(
        userId,
        (links: LienResponse[]) => {
          links.forEach(link => {
            if (link.s3Key?.includes('.svg') && link.imageBase64) {
              link.imageBase64 = link.imageBase64.replace(
                'data:application/octet-stream',
                'data:image/svg+xml'
              );
            }
          });
          this.userLinks = links;
        },
        () => {
          console.error('Failed to fetch links');
        }
      );
    },

    async createUserLink(linkData: {
      name: string;
      url: string;
      userId: string;
      file?: File;
    }) {
      await lienUtilService.createLink(
        linkData,
        async () => {
          await this.fetchUserLinks();
          console.log('Link created');
        },
        () => {
          console.error('Failed to create link');
        }
      );
    },
    async updateUserLink(linkData: {
      id: string;
      name?: string;
      url?: string;
      userId?: string;
      file?: File;
    }) {
      await lienUtilService.updateLink(
        linkData.id,
        linkData,
        async () => {
          await this.fetchUserLinks();
          console.log('Link Updated');
        },
        () => {
          console.error('Failed to Update link');
        }
      );
    },

    async deleteUserLink(linkId: string) {
      await lienUtilService.deleteLink(
        linkId,
        async () => {
          await this.fetchUserLinks();
          console.log('Link deleted');
        },
        () => {
          console.error('Failed to delete link');
        }
      );
    },
  },
});
